import React, { useRef, useState, useEffect } from "react";
import { Button, Input, Space, Table, DatePicker } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import moment from "moment";
const { RangePicker } = DatePicker;

const SkeletonTable = ({
  columns = [],
  searchingFields = [],
  dateFilterFields = [],
  data = [],
  handleTableChange = () => {},
  isLoading,
  pagination = false,
  index = true,
  onRowClick = () => {},
}) => {
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const initialPage =
    typeof pagination === "object" && pagination?.current
      ? pagination.current
      : 1;
  const initialPageSize =
    typeof pagination === "object" && pagination?.pageSize
      ? pagination.pageSize
      : 10;
  const savedPageRef = useRef(initialPage);
  const [paginationConfig, setPaginationConfig] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  useEffect(() => {
    if (typeof pagination === "object" && pagination?.current) {
      setPaginationConfig((prev) => ({
        ...prev,
        current: pagination.current,
        pageSize: pagination.pageSize || prev.pageSize,
      }));
      savedPageRef.current = pagination.current;
    }
  }, [pagination]);

  const numberColumn = {
    title: "No.",
    dataIndex: "no",
    render: (_, __, index) => {
      const current =
        (pagination &&
          typeof pagination === "object" &&
          pagination.current) ||
        paginationConfig.current ||
        1;
      const pageSize =
        (pagination &&
          typeof pagination === "object" &&
          pagination.pageSize) ||
        paginationConfig.pageSize ||
        10;
      return (current - 1) * pageSize + index + 1;
    },
    width: 80,
    fixed: "left",
  };

  const newColumns = index ? [numberColumn, ...columns] : columns;

  // ---------- Column wise search functionality ------------ //
  const handleSearchInter = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const searchVal = selectedKeys && selectedKeys[0];
    if (!searchVal) {
      setSearchText("");
      setSearchedColumn("");
      setPaginationConfig((prev) => ({
        ...prev,
        current: savedPageRef.current || 1,
      }));
    } else {
      setSearchText(searchVal);
      setSearchedColumn(dataIndex);
      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
      }));
    }
  };

  const handleReset = (clearFilters, confirm) => {
    if (clearFilters) {
      clearFilters({ confirm: true, closeDropdown: true });
    } else if (confirm) {
      confirm();
    }
    setSearchText("");
    setSearchedColumn("");
    setPaginationConfig((prev) => ({
      ...prev,
      current: savedPageRef.current || 1,
    }));
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="p-2" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearchInter(selectedKeys, confirm, dataIndex)
          }
          className="mb-2 block"
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSearchInter(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={() => handleReset(clearFilters, confirm)}
          >
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={`${filtered ? "primary" : ""}`} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value?.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getDateFilterProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div className="p-2">
        <RangePicker
          value={selectedKeys.length ? selectedKeys[0] : null}
          onChange={(dates) => {
            setSelectedKeys(dates ? [dates] : []);
            confirm();
          }}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={`${filtered ? "primary" : ""}`} />
    ),
    onFilter: (dates, record) => {
      if (!dates?.length) return true;
      const [startDate, endDate] = dates.map((date) => moment(date?.toDate()));
      const recordDate = moment(record[dataIndex]);
      return recordDate.isBetween(startDate, endDate, "day", "[]");
    },
  });

  const updatedColumns = newColumns?.map((column) => {
    if (searchingFields?.includes(column?.dataIndex)) {
      return { ...column, ...getColumnSearchProps(column?.dataIndex) };
    }
    if (dateFilterFields?.includes(column?.dataIndex)) {
      return { ...column, ...getDateFilterProps(column?.dataIndex) };
    }
    return column;
  });

  const mergedPagination =
    pagination === false
      ? false
      : {
          current:
            typeof pagination === "object" && pagination.current
              ? pagination.current
              : paginationConfig.current,
          pageSize:
            typeof pagination === "object" && pagination.pageSize
              ? pagination.pageSize
              : paginationConfig.pageSize,
          showSizeChanger: true,
          ...(typeof pagination === "object" ? pagination : {}),
        };

  return (
    <Table
      className="table-responsive"
      columns={updatedColumns}
      dataSource={data}
      loading={isLoading}
      sortDirections={false}
      pagination={mergedPagination}
      onChange={(newPagination, filters, sorter, extra) => {
        handleTableChange(newPagination, filters, sorter, extra);
        if (extra?.action === "paginate" || !extra?.action) {
          savedPageRef.current = newPagination.current;
          setPaginationConfig((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
          }));
        }
      }}
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: (e) => {
          onRowClick(record);
        },
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default SkeletonTable;
